import { User } from '../types';
import {
  cachedPatientService,
  cachedPriorAuthService,
  cachedPayerService,
  cachedDashboardService,
  cachedInsuranceService
} from './cachedApiService';

/**
 * Service to prefetch and cache data when a user logs in
 * This improves the user experience by having data ready when they navigate to different pages
 */
export const prefetchService = {
  /**
   * Prefetch data based on user role
   * @param user The logged in user
   */
  prefetchDataForUser: async (user: User | null): Promise<void> => {
    if (!user) return;

    console.log(`Prefetching data for ${user.role} user (${user.username})...`);
    console.log('This will prepare data for faster navigation');
    
    try {
      // Prefetch dashboard stats for all users
      await cachedDashboardService.getDashboardStats();
      
      // Role-specific prefetching
      switch (user.role) {
        case 'provider':
          await prefetchProviderData(user);
          break;
        case 'payer':
          await prefetchPayerData();
          break;
        case 'superuser':
          await prefetchAdminData();
          break;
        case 'patient':
          await prefetchPatientData(user);
          break;
      }
      
      console.log('✅ All prefetching completed successfully');
      console.log('The app should now navigate faster between pages');
    } catch (error) {
      console.error('❌ Error during prefetching:', error);
      console.log('The app will still work, but might be slower on first page loads');
      // Don't throw the error - prefetching is a background operation
      // that shouldn't block the user experience if it fails
    }
  }
};

/**
 * Prefetch data for provider users
 */
async function prefetchProviderData(user: User): Promise<void> {
  console.log('Starting provider data prefetching...');
  
  try {
    // First, get the list of assigned patients and all patients
    const [assignedPids, allPatients] = await Promise.all([
      cachedPatientService.getAssignedPatients(user.username),
      cachedPatientService.getAllPatients()
    ]);
    
    console.log(`Prefetched ${assignedPids.length} assigned patients and ${allPatients.length} total patients`);
    
    // Filter to get only the assigned patients
    const providerPatients = allPatients.filter(patient => assignedPids.includes(patient.pid));
    
    // Prefetch other data in parallel
    await Promise.all([
      // Prefetch prior authorizations
      cachedPriorAuthService.getAllPriorAuthorizations(),
      
      // Prefetch insurance data (needed for patient list to check ePA status)
      cachedInsuranceService.getAllInsurance()
    ]);
    
    console.log('Prefetched prior authorizations and insurance data');
    
    // Now prefetch patient details for each assigned patient
    // This is needed for the RequestList component
    console.log(`Prefetching details for ${providerPatients.length} patients...`);
    
    const patientDetailPromises = providerPatients.map(patient =>
      cachedPatientService.getPatientDetail(patient.pid)
    );
    
    await Promise.all(patientDetailPromises);
    
    console.log('Prefetched all patient details');
  } catch (error) {
    console.error('Error during provider data prefetching:', error);
  }
  
  console.log('Provider data prefetching complete');
}

/**
 * Prefetch data for payer users
 */
async function prefetchPayerData(): Promise<void> {
  console.log('Starting payer data prefetching...');
  
  try {
    // Run these in parallel for better performance
    const [pendingAuths, reviewedAuths, insurancePlans, allInsurance] = await Promise.all([
      // Prefetch pending authorizations
      cachedPayerService.getPendingAuthorizations(),
      
      // Prefetch reviewed authorizations
      cachedPayerService.getReviewedAuthorizations(),
      
      // Prefetch insurance plans
      cachedPayerService.getInsurancePlans(),
      
      // Prefetch all insurance data
      cachedInsuranceService.getAllInsurance()
    ]);
    
    console.log(`Prefetched ${pendingAuths.length} pending authorizations`);
    console.log(`Prefetched ${reviewedAuths.length} reviewed authorizations`);
    console.log(`Prefetched ${insurancePlans.length} insurance plans`);
    console.log(`Prefetched ${allInsurance.length} insurance records`);
    
    // Get patient details for patients with pending authorizations
    const patientIds = new Set<string>();
    pendingAuths.forEach(auth => patientIds.add(auth.patient));
    
    console.log(`Prefetching details for ${patientIds.size} patients with pending authorizations...`);
    
    const patientDetailPromises = Array.from(patientIds).map(patientId =>
      cachedPatientService.getPatientDetail(patientId)
    );
    
    await Promise.all(patientDetailPromises);
    
    console.log('Prefetched all patient details for pending authorizations');
  } catch (error) {
    console.error('Error during payer data prefetching:', error);
  }
  
  console.log('Payer data prefetching complete');
}

/**
 * Prefetch data for admin users
 */
async function prefetchAdminData(): Promise<void> {
  console.log('Starting admin data prefetching...');
  
  try {
    // Run these in parallel for better performance
    const [allPatients, allPriorAuths, allInsurance] = await Promise.all([
      // Prefetch patient list
      cachedPatientService.getAllPatients(),
      
      // Prefetch prior authorizations
      cachedPriorAuthService.getAllPriorAuthorizations(),
      
      // Prefetch insurance data
      cachedInsuranceService.getAllInsurance()
    ]);
    
    console.log(`Prefetched ${allPatients.length} patients`);
    console.log(`Prefetched ${allPriorAuths.length} prior authorizations`);
    console.log(`Prefetched ${allInsurance.length} insurance records`);
    
    // For admins, we'll prefetch details for a limited number of patients
    // to avoid too many requests
    const patientsToFetch = allPatients.slice(0, 10); // Fetch first 10 patients
    
    console.log(`Prefetching details for ${patientsToFetch.length} patients...`);
    
    const patientDetailPromises = patientsToFetch.map(patient =>
      cachedPatientService.getPatientDetail(patient.pid)
    );
    
    await Promise.all(patientDetailPromises);
    
    console.log('Prefetched patient details for first 10 patients');
  } catch (error) {
    console.error('Error during admin data prefetching:', error);
  }
  
  console.log('Admin data prefetching complete');
}

/**
 * Prefetch data for patient users
 */
async function prefetchPatientData(user: User): Promise<void> {
  if (!user.id) return;
  
  console.log('Starting patient data prefetching...');
  
  try {
    // Run these in parallel for better performance
    const [patientDetail, patientPriorAuths, allInsurance] = await Promise.all([
      // Prefetch patient details
      cachedPatientService.getPatientDetail(user.id),
      
      // Prefetch patient's prior authorizations
      cachedPriorAuthService.getPatientPriorAuthorizations(user.id),
      
      // Prefetch insurance data (might be needed for patient portal)
      cachedInsuranceService.getAllInsurance()
    ]);
    
    console.log(`Prefetched details for patient ${user.id}`);
    console.log(`Prefetched ${patientPriorAuths.length} prior authorizations for patient`);
    console.log(`Prefetched ${allInsurance.length} insurance records`);
  } catch (error) {
    console.error('Error during patient data prefetching:', error);
  }
  
  console.log('Patient data prefetching complete');
}